import React, { useEffect, useRef, useState } from 'react';

import type { AutocompleteState } from '@algolia/autocomplete-core';
import type { SearchOptions } from '@algolia/client-search';
import classNames from 'classnames';

import type { DocSearchHit } from '../../../../types';
import { Footer } from '../Footer';
import { ScreenState } from '../ScreenState';
import { SearchBox } from '../SearchBox';
import { useAutoComplete } from '../useAutoComplete';
import { useTouchEvents } from '../useTouchEvents';
import { useTrapFocus } from '../useTrapFocus';

import * as classes from './style.module.scss';

export type DocSearchModalProps = {
  apiKey: string;
  appId: string;
  indexName: string;
  placeholder?: string;
  initialScrollY: number;
  searchParameters: SearchOptions;
  onClose?: () => void;
};

export const DocSearchModal: React.FC<DocSearchModalProps> = ({
  appId,
  apiKey,
  indexName,
  placeholder = 'Search docs',
  searchParameters,
  onClose = () => null,
  initialScrollY = 0,
}) => {
  const [state, setState] = useState<AutocompleteState<DocSearchHit>>({
    query: '',
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    activeItemId: null,
    status: 'idle',
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const formElementRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const snippetLength = useRef<number>(10);
  const initialQueryFromSelection = useRef(
    typeof window !== 'undefined' && window.getSelection() !== null
      ? // casting here since we check !null before this is used
        (window.getSelection() as Selection).toString().slice(0, 64)
      : '',
  ).current;
  const initialQuery = useRef(initialQueryFromSelection).current;

  const { autocomplete, favoriteSearches, recentSearches, saveRecentSearch } =
    useAutoComplete(
      appId,
      apiKey,
      indexName,
      searchParameters,
      onClose,
      initialQuery,
      placeholder,
      snippetLength,
      setState,
    );

  const { getEnvironmentProps, getRootProps, refresh } = autocomplete;

  useTouchEvents({
    getEnvironmentProps,
    panelElement: dropdownRef.current,
    formElement: formElementRef.current,
    inputElement: inputRef.current,
  });
  useTrapFocus({ container: containerRef.current });

  useEffect(() => {
    document.body.classList.add('DocSearch--active');

    return () => {
      document.body.classList.remove('DocSearch--active');

      // IE11 doesn't support `scrollTo` so we check that the method exists
      // first.
      window.scrollTo?.(0, initialScrollY);
    };
  }, []);

  useEffect(() => {
    const isMobileMediaQuery = window.matchMedia('(max-width: 768px)');

    if (isMobileMediaQuery.matches) {
      snippetLength.current = 5;
    }
  }, []);

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.scrollTop = 0;
    }
  }, [state.query]);

  // We don't focus the input when there's an initial query (i.e. Selection
  // Search) because users rather want to see the results directly, without the
  // keyboard appearing.
  // We therefore need to refresh the autocomplete instance to load all the
  // results, which is usually triggered on focus.
  useEffect(() => {
    if (initialQuery.length > 0) {
      refresh();

      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [initialQuery, refresh]);

  // We rely on a CSS property to set the modal height to the full viewport height
  // because all mobile browsers don't compute their height the same way.
  // See https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
  useEffect(() => {
    function setFullViewportHeight() {
      if (modalRef.current) {
        const vh = window.innerHeight * 0.01;
        modalRef.current.style.setProperty('--docsearch-vh', `${vh}px`);
      }
    }

    setFullViewportHeight();

    window.addEventListener('resize', setFullViewportHeight);

    return () => {
      window.removeEventListener('resize', setFullViewportHeight);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      {...getRootProps({
        'aria-expanded': true,
      })}
      className={classNames('DocSearch', 'DocSearch-Container', {
        'DocSearch-Container--Stalled': state.status === 'stalled',
        'DocSearch-Container--Errored': state.status === 'error',
      })}
      role="button"
      tabIndex={0}
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="DocSearch-Modal" ref={modalRef}>
        <header className="DocSearch-SearchBar" ref={formElementRef}>
          <SearchBox
            {...autocomplete}
            state={state}
            autoFocus={initialQuery.length === 0}
            inputRef={inputRef}
            isFromSelection={
              Boolean(initialQuery) &&
              initialQuery === initialQueryFromSelection
            }
            onClose={onClose}
          />
        </header>
        <div className="DocSearch-Dropdown" ref={dropdownRef}>
          <ScreenState
            {...autocomplete}
            indexName={indexName}
            state={state}
            recentSearches={recentSearches}
            favoriteSearches={favoriteSearches}
            inputRef={inputRef}
            onItemClick={item => {
              saveRecentSearch(item);
              onClose();
            }}
          />
        </div>
        <footer className={classNames(classes.footer, 'DocSearch-Footer')}>
          <Footer />
        </footer>
      </div>
    </div>
  );
};
